import { inject, Injectable } from "@angular/core";
import {
  EventComboTicket,
  EventComboTicketRequestBody,
} from "@models/combo-ticket";
import { firstValueFrom } from "rxjs";
import { ComboTicketManagementService } from "./combo-ticket-management.service";

function comboSaveError(error: unknown): string {
  const body = (error as { error?: { errors?: string[] | string; error?: string } })
    ?.error;
  const details = body?.errors ?? body?.error;
  if (Array.isArray(details) && details.length) {
    return `: ${details.join(", ")}`;
  }
  if (typeof details === "string" && details.trim()) {
    return `: ${details}`;
  }
  return "";
}

export function comboRequest(
  combo: EventComboTicket,
): EventComboTicketRequestBody {
  return {
    title: combo.title,
    description: combo.description,
    quantity: Number(combo.quantity),
    price: Number(combo.price),
    min_per_buyer: Number(combo.min_per_buyer || 1),
    max_per_buyer: Number(combo.max_per_buyer || 5),
    availability: combo.availability || "general public",
    component_ticket_ids: combo.component_ticket_ids,
    component_tickets: (combo.component_tickets ?? [])
      .filter((ticket) => Number(ticket.id) > 0)
      .map((ticket) => ({
        id: Number(ticket.id),
        quantity: Math.max(1, Number(ticket.quantity ?? 1)),
      })),
    ...(combo.sale_start ? { sale_start: combo.sale_start } : {}),
    ...(combo.sale_end ? { sale_end: combo.sale_end } : {}),
    ...(combo.source_batch_id ? { source_batch_id: combo.source_batch_id } : {}),
  };
}

export interface ComboSyncResult {
  combos: EventComboTicket[];
  persisted: EventComboTicket[];
  failures: string[];
}

@Injectable({ providedIn: "root" })
export class EventCombosSyncService {
  private readonly api = inject(ComboTicketManagementService);

  async sync(
    eventId: number,
    current: EventComboTicket[],
    original: EventComboTicket[],
  ): Promise<ComboSyncResult> {
    const result: ComboSyncResult = {
      combos: [...current],
      persisted: original.map((combo) => ({ ...combo })),
      failures: [],
    };

    for (const saved of original) {
      if (saved.id && !current.some((combo) => combo.id === saved.id)) {
        try {
          await firstValueFrom(this.api.deleteComboTicket(saved.id));
        } catch {
          result.failures.push(`Remover ingresso combo "${saved.title}"`);
        }
      }
    }

    const next: EventComboTicket[] = [];
    for (const combo of current) {
      const previous = combo.id
        ? original.find((item) => item.id === combo.id)
        : undefined;
      if (
        previous &&
        JSON.stringify(comboRequest(previous)) ===
          JSON.stringify(comboRequest(combo))
      ) {
        next.push(combo);
        continue;
      }
      if (
        !combo.id &&
        (combo.component_tickets ?? []).reduce(
          (total, ticket) => total + Math.max(1, Number(ticket.quantity ?? 1)),
          0,
        ) < 2
      ) {
        result.failures.push(`Salvar ingresso combo "${combo.title}"`);
        next.push(combo);
        continue;
      }
      try {
        const response = await firstValueFrom(
          combo.id
            ? this.api.updateComboTicket(combo.id, comboRequest(combo))
            : this.api.createComboTicket(eventId, comboRequest(combo)),
        );
        next.push({ ...combo, ...response, id: response.id ?? combo.id });
      } catch (error) {
        result.failures.push(
          `Salvar ingresso combo "${combo.title}"${comboSaveError(error)}`,
        );
        next.push(combo);
      }
    }

    result.combos = next;
    result.persisted = next.filter((combo) => combo.id);
    return result;
  }
}
